/**
 * Cloud File Tree Component
 *
 * Displays a hierarchical tree view of files and folders from cloud storage.
 * Supports single and multi-selection, lazy loading, and folder navigation.
 *
 * Supports both controlled and uncontrolled modes:
 * - Uncontrolled: Component manages its own selection state internally
 * - Controlled: Parent manages selection via selectedFiles/selectedFolders props
 *
 * Lazy Loading:
 * - Initial tree is loaded up to maxDepth
 * - Folders beyond maxDepth are loaded on-demand when expanded
 * - Each folder shows loading/error states independently
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Checkbox,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { FileIcon } from "./FileIcon";
import type {
  CloudTreeNode,
  CloudFileMetadata,
  CloudProviderType,
} from "src/types/cloudFiles";
import {
  useCloudTree,
  fetchCloudSubtree,
  formatFileSize,
  formatDate,
  getFileExtension,
} from "../services/CloudFileServices";
import { getParentPath, isRootPath } from "../utils/pathUtils";

// =============================================================================
// Types
// =============================================================================

/**
 * Loading state for a folder node during lazy loading.
 */
type NodeLoadingStatus = "idle" | "loading" | "loaded" | "error";

interface NodeLoadingState {
  status: NodeLoadingStatus;
  error?: string;
}

interface CloudFileTreeProps {
  /** Cloud provider type */
  providerType: CloudProviderType;
  /** External connection ID */
  connectionId: number;
  /** Root path to display (default: "/") */
  rootPath?: string;
  /** Maximum depth to load initially */
  maxDepth?: number;
  /** Selection mode */
  selectionMode?: "none" | "single" | "multiple";
  /** Callback when a file is selected/clicked */
  onFileSelect?: (file: CloudFileMetadata) => void;
  /** Callback when selection changes (all selected items) - enables controlled mode */
  onSelectionChange?: (
    files: CloudFileMetadata[],
    folders: CloudFileMetadata[],
  ) => void;
  /** Callback when a folder is double-clicked (for navigation) */
  onFolderNavigate?: (folder: CloudFileMetadata) => void;
  /** Whether to show file details (size, date) */
  showDetails?: boolean;
  /** Custom height for the tree container */
  height?: string | number;
  /** Controlled: externally managed selected files */
  selectedFiles?: CloudFileMetadata[];
  /** Controlled: externally managed selected folders */
  selectedFolders?: CloudFileMetadata[];
  /** Default expanded paths (uncontrolled) */
  defaultExpanded?: string[];
  /** Focused path - tree will expand to and scroll to this path */
  focusedPath?: string;
  /**
   * Map of folder path → allowed file extensions for that folder's descendants.
   * Files whose ancestor appears in this map are hidden unless their extension
   * is in the folder's allowed list. Folders themselves are never hidden.
   */
  folderExtensionFilters?: Map<string, string[]>;
}

// =============================================================================
// Tree Item Label Component
// =============================================================================

interface TreeItemLabelProps {
  node: CloudTreeNode;
  isSelected: boolean;
  isIndeterminate: boolean;
  selectionMode: "none" | "single" | "multiple";
  showDetails: boolean;
  onToggleSelect: () => void;
  onDoubleClick?: () => void;
}

const TreeItemLabel: React.FC<TreeItemLabelProps> = ({
  node,
  isSelected,
  isIndeterminate,
  selectionMode,
  showDetails,
  onToggleSelect,
  onDoubleClick,
}) => {
  const { metadata } = node;
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastClickTimeRef = useRef<number>(0);
  const lastClickPathRef = useRef<string>("");

  // Handle click - track clicks manually since MUI eats the native double-click
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      const now = Date.now();
      const timeSinceLastClick = now - lastClickTimeRef.current;
      const isSamePath = lastClickPathRef.current === metadata.path;

      // Check if this is a double-click (same path clicked within 400ms)
      if (isSamePath && timeSinceLastClick < 400) {
        // Reset tracking
        lastClickTimeRef.current = 0;
        lastClickPathRef.current = "";
        // Cancel any pending single-click timer
        if (clickTimerRef.current) {
          clearTimeout(clickTimerRef.current);
          clickTimerRef.current = null;
        }
        // Trigger navigation
        if (onDoubleClick) {
          onDoubleClick();
        }
        return;
      }

      // Track this click for potential double-click
      lastClickTimeRef.current = now;
      lastClickPathRef.current = metadata.path;

      // Single click - delay to allow for double-click
      if (selectionMode === "none") return;

      // Clear any existing timer
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }

      // Set timer - if no double-click within 300ms, toggle selection
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        onToggleSelect();
      }, 300);
    },
    [
      selectionMode,
      onToggleSelect,
      onDoubleClick,
      metadata.name,
      metadata.path,
    ],
  );

  // Build tooltip content with full metadata
  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
        {metadata.name}
      </Typography>
      <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
        {metadata.path}
      </Typography>
      {!metadata.is_directory && (
        <>
          <Typography variant="caption" display="block">
            Size: {formatFileSize(metadata.size)}
          </Typography>
          <Typography variant="caption" display="block">
            Modified: {formatDate(metadata.modified_date)}
          </Typography>
          <Typography variant="caption" display="block">
            Type: {metadata.content_type}
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement="right" arrow enterDelay={500}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 0.5,
          px: 1,
          borderRadius: 1,
          bgcolor: isSelected ? "action.selected" : "transparent",
          "&:hover": {
            bgcolor: isSelected ? "action.selected" : "transparent",
          },
        }}
        onClick={handleClick}
      >
        {selectionMode === "multiple" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: 0.5,
            }}
          >
            <Checkbox
              checked={isSelected}
              indeterminate={isIndeterminate && !isSelected}
              size="small"
              sx={{ p: 0 }}
              data-testid={`cloud-tree-checkbox-${metadata.path.replace(
                /\//g,
                "--",
              )}`}
              onClick={(e) => {
                e.stopPropagation();
                // Direct checkbox click - no double-click detection needed
                onToggleSelect();
              }}
            />
          </Box>
        )}

        <FileIcon
          filename={metadata.name}
          isDirectory={metadata.is_directory}
        />

        <Typography
          variant="body2"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {metadata.name}
        </Typography>

        {/* Enhanced metadata display for files */}
        {showDetails && !metadata.is_directory && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              ml: 2,
              flexShrink: 0,
            }}
          >
            {/* Modified date */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 85 }}
            >
              {formatDate(metadata.modified_date)}
            </Typography>

            {/* File size */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 55, textAlign: "right" }}
            >
              {formatFileSize(metadata.size)}
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

// =============================================================================
// Node Loading Indicator Component
// =============================================================================

interface NodeLoadingIndicatorProps {
  status: "loading" | "error";
  error?: string;
  onRetry?: () => void;
}

const NodeLoadingIndicator: React.FC<NodeLoadingIndicatorProps> = ({
  status,
  error,
  onRetry,
}) => {
  if (status === "loading") {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, px: 1 }}
      >
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, px: 1 }}
      >
        <ErrorOutlineIcon color="error" fontSize="small" />
        <Tooltip title={error || "Failed to load"}>
          <Typography variant="body2" color="error" sx={{ flex: 1 }}>
            Failed to load
          </Typography>
        </Tooltip>
        {onRetry && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
            sx={{ ml: 1 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  return null;
};

// =============================================================================
// Recursive Tree Renderer
// =============================================================================

interface RenderTreeProps {
  nodes: CloudTreeNode[];
  selectionMode: "none" | "single" | "multiple";
  showDetails: boolean;
  selectedFiles: Map<string, CloudFileMetadata>;
  selectedFolders: Map<string, CloudFileMetadata>;
  /** Set of all selected paths (files + folders) for indeterminate calculation */
  allSelectedPaths: Set<string>;
  onToggleSelect: (node: CloudTreeNode) => void;
  onFolderNavigate?: (folder: CloudFileMetadata) => void;
  expandedNodes: Set<string>;
  nodeLoadingStates: Map<string, NodeLoadingState>;
  onRetryNode: (path: string) => void;
  folderExtensionFilters?: Map<string, string[]>;
  /**
   * Filter inherited from outside the rendered subtree (e.g. when the tree is
   * focused into a folder that has its own filter). Used as a fallback when
   * a file has no filtered ancestor in its parent chain.
   */
  inheritedExtensionFilter?: string[];
}

/**
 * Return the allowed extensions of the nearest ancestor folder that has
 * an active extension filter, or undefined if no ancestor is filtered.
 */
const findAncestorExtensionFilter = (
  node: CloudTreeNode,
  folderExtensionFilters: Map<string, string[]>,
): string[] | undefined => {
  let current = node.parent;
  while (current) {
    const allowed = folderExtensionFilters.get(current.metadata.path);
    if (allowed && allowed.length > 0) {
      return allowed;
    }
    current = current.parent;
  }
  return undefined;
};

/**
 * Walk a path up through its ancestor paths, returning the first active
 * filter found. Used to derive the inherited filter for a focused-path
 * root when its parent chain isn't present in the rendered subtree.
 */
const findFilterForPath = (
  path: string,
  folderExtensionFilters: Map<string, string[]>,
): string[] | undefined => {
  let current = path;
  while (!isRootPath(current)) {
    const allowed = folderExtensionFilters.get(current);
    if (allowed && allowed.length > 0) return allowed;
    current = getParentPath(current);
  }
  const rootFilter = folderExtensionFilters.get("/");
  return rootFilter && rootFilter.length > 0 ? rootFilter : undefined;
};

/**
 * True when the node should be rendered given the active extension filters.
 * Folders are always visible; files are hidden when their closest filtered
 * ancestor (or the inherited filter when the parent chain is exhausted)
 * rejects their extension.
 */
const isNodeVisibleUnderExtensionFilters = (
  node: CloudTreeNode,
  folderExtensionFilters: Map<string, string[]> | undefined,
  inheritedExtensionFilter: string[] | undefined,
): boolean => {
  if (!folderExtensionFilters || folderExtensionFilters.size === 0) return true;
  if (node.metadata.is_directory) return true;
  const allowed =
    findAncestorExtensionFilter(node, folderExtensionFilters) ??
    inheritedExtensionFilter;
  if (!allowed) return true;
  return allowed.includes(getFileExtension(node.metadata.name));
};

// =============================================================================
// Selection Helper Functions
// =============================================================================

/**
 * Check if a node has a selected ancestor folder.
 * Uses parent refs for tree traversal.
 */
const isChildOfSelectedFolder = (
  node: CloudTreeNode,
  selectedFolders: Map<string, CloudFileMetadata>,
): boolean => {
  let current = node.parent;
  while (current) {
    if (selectedFolders.has(current.metadata.path)) {
      return true;
    }
    current = current.parent;
  }
  return false;
};

/**
 * Check if a node has any selected ancestor from the given array.
 */
const isChildOfAnyFolder = (
  node: CloudTreeNode,
  folders: CloudFileMetadata[],
): boolean => {
  const folderPaths = new Set(folders.map((f) => f.path));
  let current = node.parent;
  while (current) {
    if (folderPaths.has(current.metadata.path)) {
      return true;
    }
    current = current.parent;
  }
  return false;
};

/**
 * Find the closest selected ancestor folder node.
 * Returns the most immediate (deepest) selected ancestor.
 */
const findSelectedAncestor = (
  node: CloudTreeNode,
  selectedFolders: CloudFileMetadata[],
): CloudTreeNode | undefined => {
  const folderPaths = new Set(selectedFolders.map((f) => f.path));
  let current = node.parent;
  while (current) {
    if (folderPaths.has(current.metadata.path)) {
      return current;
    }
    current = current.parent;
  }
  return undefined;
};

/**
 * Get sibling nodes (other children of the same parent).
 */
const getSiblings = (node: CloudTreeNode): CloudTreeNode[] => {
  if (!node.parent || !node.parent.children) {
    return [];
  }
  return node.parent.children.filter(
    (c) => c.metadata.path !== node.metadata.path,
  );
};

/**
 * Check if a path is a descendant of a folder path.
 * Normalizes paths to handle double slashes.
 */
const isDescendantOf = (
  childPath: string,
  parentFolderPath: string,
): boolean => {
  const normalizedChild = childPath.replace(/\/+/g, "/");
  const normalizedParent =
    parentFolderPath.replace(/\/+/g, "/").replace(/\/$/, "") + "/";
  return normalizedChild.startsWith(normalizedParent);
};

/**
 * Check if all children of a node are selected (directly or via the selection arrays).
 * Returns true if the node has children and ALL of them are in the selection.
 */
const areAllChildrenSelected = (
  node: CloudTreeNode,
  selectedFiles: CloudFileMetadata[],
  selectedFolders: CloudFileMetadata[],
): boolean => {
  if (!node.children || node.children.length === 0) {
    return false; // No children to check
  }

  const selectedFilePaths = new Set(selectedFiles.map((f) => f.path));
  const selectedFolderPaths = new Set(selectedFolders.map((f) => f.path));

  for (const child of node.children) {
    const childPath = child.metadata.path;
    if (child.metadata.is_directory) {
      if (!selectedFolderPaths.has(childPath)) {
        return false;
      }
    } else {
      if (!selectedFilePaths.has(childPath)) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Promote selection to parent when all children are selected.
 * Recursively checks up the tree and promotes as needed.
 * Returns the new files and folders arrays after promotion.
 */
const promoteSelectionToParent = (
  node: CloudTreeNode,
  files: CloudFileMetadata[],
  folders: CloudFileMetadata[],
): { files: CloudFileMetadata[]; folders: CloudFileMetadata[] } => {
  let newFiles = files;
  let newFolders = folders;

  // Start from the node and walk up checking for promotion
  let current: CloudTreeNode | undefined = node;

  while (current?.parent) {
    const parentNode: CloudTreeNode = current.parent;

    // Check if all children of the parent are now selected
    if (areAllChildrenSelected(parentNode, newFiles, newFolders)) {
      // Remove all children from selection
      const childPaths = new Set<string>(
        parentNode.children?.map((c: CloudTreeNode) => c.metadata.path) || [],
      );

      newFiles = newFiles.filter((f) => !childPaths.has(f.path));
      newFolders = newFolders.filter((f) => !childPaths.has(f.path));

      // Add parent to selection
      newFolders = [...newFolders, parentNode.metadata];

      // Continue checking up the tree
      current = parentNode;
    } else {
      // Can't promote further
      break;
    }
  }

  return { files: newFiles, folders: newFolders };
};

// =============================================================================
// Tree Rendering
// =============================================================================

const renderTree = ({
  nodes,
  selectionMode,
  showDetails,
  selectedFiles,
  selectedFolders,
  allSelectedPaths,
  onToggleSelect,
  onFolderNavigate,
  expandedNodes,
  nodeLoadingStates,
  onRetryNode,
  folderExtensionFilters,
  inheritedExtensionFilter,
}: RenderTreeProps): React.ReactNode => {
  const visibleNodes = nodes.filter((node) =>
    isNodeVisibleUnderExtensionFilters(
      node,
      folderExtensionFilters,
      inheritedExtensionFilter,
    ),
  );
  return visibleNodes.map((node) => {
    const { metadata, children } = node;

    // Check direct selection
    const isDirectlySelected = metadata.is_directory
      ? selectedFolders.has(metadata.path)
      : selectedFiles.has(metadata.path);

    // Check implicit selection (child of a selected folder)
    const isImplicitlySelected = isChildOfSelectedFolder(node, selectedFolders);

    // Item is selected if directly or implicitly selected
    const isSelected = isDirectlySelected || isImplicitlySelected;

    // Calculate indeterminate state for directories:
    // A folder is indeterminate if some (but not all) descendants are selected
    // and the folder itself is not selected
    let isIndeterminate = false;
    if (metadata.is_directory && !isSelected && allSelectedPaths.size > 0) {
      // Normalize the folder path (remove double slashes, ensure trailing slash)
      const normalizedFolderPath =
        metadata.path.replace(/\/+/g, "/").replace(/\/$/, "") + "/";

      for (const selectedPath of allSelectedPaths) {
        // Normalize the selected path as well
        const normalizedSelectedPath = selectedPath.replace(/\/+/g, "/");
        if (normalizedSelectedPath.startsWith(normalizedFolderPath)) {
          isIndeterminate = true;
          break;
        }
      }
    }

    // Get loading state for this folder
    const nodeState = nodeLoadingStates.get(metadata.path);
    const isLoading = nodeState?.status === "loading";
    const hasError = nodeState?.status === "error";
    const isLoaded = nodeState?.status === "loaded";

    // Determine what to show as children for directories
    const renderChildren = () => {
      // Has loaded children - render them
      if (children && children.length > 0) {
        return renderTree({
          nodes: children,
          selectionMode,
          showDetails,
          selectedFiles,
          selectedFolders,
          allSelectedPaths,
          onToggleSelect,
          onFolderNavigate,
          expandedNodes,
          nodeLoadingStates,
          onRetryNode,
          folderExtensionFilters,
          inheritedExtensionFilter,
        });
      }

      // Is currently loading
      if (isLoading) {
        return (
          <TreeItem
            itemId={`${metadata.path}/__loading__`}
            label={<NodeLoadingIndicator status="loading" />}
          />
        );
      }

      // Has error - show error with retry
      if (hasError) {
        return (
          <TreeItem
            itemId={`${metadata.path}/__error__`}
            label={
              <NodeLoadingIndicator
                status="error"
                error={nodeState?.error}
                onRetry={() => onRetryNode(metadata.path)}
              />
            }
          />
        );
      }

      // Loaded but empty
      if (isLoaded) {
        return (
          <TreeItem
            itemId={`${metadata.path}/__empty__`}
            label={
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic", py: 0.5 }}
              >
                Empty folder
              </Typography>
            }
          />
        );
      }

      // Not yet loaded (idle) - placeholder that will trigger lazy load on expand
      return (
        <TreeItem
          itemId={`${metadata.path}/__placeholder__`}
          label={
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", py: 0.5 }}
            >
              Click to load...
            </Typography>
          }
        />
      );
    };

    return (
      <TreeItem
        key={metadata.path}
        itemId={metadata.path}
        data-path={metadata.path}
        data-testid={`cloud-tree-node-${metadata.path.replace(/\//g, "--")}`}
        label={
          <TreeItemLabel
            node={node}
            isSelected={isSelected}
            isIndeterminate={isIndeterminate}
            selectionMode={selectionMode}
            showDetails={showDetails}
            onToggleSelect={() => onToggleSelect(node)}
            onDoubleClick={
              metadata.is_directory && onFolderNavigate
                ? () => onFolderNavigate(metadata)
                : undefined
            }
          />
        }
      >
        {metadata.is_directory ? renderChildren() : null}
      </TreeItem>
    );
  });
};

// =============================================================================
// Loading Skeleton
// =============================================================================

const TreeSkeleton: React.FC = () => (
  <Box sx={{ p: 2 }} data-testid="cloud-tree-loading">
    {[1, 2, 3, 4, 5].map((i) => (
      <Box
        key={i}
        sx={{ display: "flex", alignItems: "center", mb: 1.5, pl: (i % 3) * 2 }}
      >
        <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
        <Skeleton
          variant="text"
          width={`${60 + Math.random() * 30}%`}
          height={24}
        />
      </Box>
    ))}
  </Box>
);

// =============================================================================
// Main Component
// =============================================================================

export const CloudFileTree: React.FC<CloudFileTreeProps> = ({
  providerType,
  connectionId,
  rootPath = "/",
  maxDepth = 3,
  selectionMode = "multiple",
  onFileSelect,
  onSelectionChange,
  onFolderNavigate,
  showDetails = true,
  height = 400,
  selectedFiles: controlledSelectedFiles,
  selectedFolders: controlledSelectedFolders,
  defaultExpanded = [],
  focusedPath,
  folderExtensionFilters,
}) => {
  // Always fetch from rootPath to cache the full tree
  const {
    data: treeData,
    isLoading,
    isError,
    error,
    refetch,
  } = useCloudTree(providerType, connectionId, rootPath, maxDepth);

  // Local state for expanded nodes
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    () => new Set(defaultExpanded),
  );

  // Local state for selection (used when uncontrolled)
  const [internalSelectedFiles, setInternalSelectedFiles] = useState<
    Map<string, CloudFileMetadata>
  >(new Map());
  const [internalSelectedFolders, setInternalSelectedFolders] = useState<
    Map<string, CloudFileMetadata>
  >(new Map());

  // Lazy loading state - track loading status per folder
  const [nodeLoadingStates, setNodeLoadingStates] = useState<
    Map<string, NodeLoadingState>
  >(new Map());

  // Lazy-loaded children - merged into tree structure
  const [lazyLoadedChildren, setLazyLoadedChildren] = useState<
    Map<string, CloudTreeNode[]>
  >(new Map());

  // Ref for the tree container (for scrolling)
  const treeContainerRef = useRef<HTMLDivElement>(null);

  // Track paths currently being loaded (ref to avoid stale closure issues)
  const loadingPathsRef = useRef<Set<string>>(new Set());

  // Find the subtree at focusedPath (if provided) from the loaded tree data
  // This also considers lazy-loaded children
  const displayTreeData = useMemo(() => {
    if (
      !treeData ||
      !focusedPath ||
      focusedPath === "/" ||
      focusedPath === rootPath
    ) {
      return treeData;
    }

    // Find the node at focusedPath, also checking lazy-loaded children
    const findNode = (
      nodes: CloudTreeNode[],
      targetPath: string,
    ): CloudTreeNode | null => {
      for (const node of nodes) {
        if (node.metadata.path === targetPath) {
          return node;
        }

        // Check both original children and lazy-loaded children
        const nodeChildren = node.children || [];
        const lazyChildren = lazyLoadedChildren.get(node.metadata.path) || [];
        const allChildren =
          nodeChildren.length > 0 ? nodeChildren : lazyChildren;

        if (
          allChildren.length > 0 &&
          targetPath.startsWith(node.metadata.path + "/")
        ) {
          const found = findNode(allChildren, targetPath);
          if (found) return found;
        }
      }
      return null;
    };

    const focusedNode = findNode(treeData, focusedPath);

    // If we found the node, return its children as the new root level
    if (focusedNode) {
      // Check for lazy-loaded children first, then original children
      const lazyChildren = lazyLoadedChildren.get(focusedPath);
      if (lazyChildren && lazyChildren.length > 0) {
        return lazyChildren;
      }
      if (focusedNode.children && focusedNode.children.length > 0) {
        return focusedNode.children;
      }
      // Node exists but has no children loaded yet - return empty array
      // The lazy loading will handle fetching children when expanded
      return [];
    }

    // Path not found in current tree - return original data
    // This shouldn't happen if navigation is working correctly
    return treeData;
  }, [treeData, focusedPath, rootPath, lazyLoadedChildren]);

  // When the tree is focused into a subfolder, its root-level nodes have no
  // parent refs, so findAncestorExtensionFilter can't reach a filter keyed
  // on the focused folder or its ancestors. Precompute that inherited filter
  // so we can use it as a fallback in the visibility check.
  const inheritedExtensionFilter = useMemo(() => {
    if (!folderExtensionFilters || folderExtensionFilters.size === 0) {
      return undefined;
    }
    if (!focusedPath || focusedPath === "/" || focusedPath === rootPath) {
      return undefined;
    }
    return findFilterForPath(focusedPath, folderExtensionFilters);
  }, [folderExtensionFilters, focusedPath, rootPath]);

  // Determine if we're in controlled mode
  const isControlled =
    controlledSelectedFiles !== undefined ||
    controlledSelectedFolders !== undefined;

  // Convert controlled arrays to Maps for efficient lookup
  const selectedFiles = useMemo(() => {
    if (isControlled && controlledSelectedFiles) {
      return new Map(controlledSelectedFiles.map((f) => [f.path, f]));
    }
    return internalSelectedFiles;
  }, [isControlled, controlledSelectedFiles, internalSelectedFiles]);

  const selectedFolders = useMemo(() => {
    if (isControlled && controlledSelectedFolders) {
      return new Map(controlledSelectedFolders.map((f) => [f.path, f]));
    }
    return internalSelectedFolders;
  }, [isControlled, controlledSelectedFolders, internalSelectedFolders]);

  // Compute all selected paths for indeterminate checkbox calculation
  const allSelectedPaths = useMemo(() => {
    const paths = new Set<string>();
    for (const path of selectedFiles.keys()) {
      paths.add(path);
    }
    for (const path of selectedFolders.keys()) {
      paths.add(path);
    }
    return paths;
  }, [selectedFiles, selectedFolders]);

  // Convert Set to array for TreeView
  const expandedArray = useMemo(
    () => Array.from(expandedNodes),
    [expandedNodes],
  );

  // Merge lazy-loaded children into tree data and set parent references
  // Use displayTreeData (which may be a subtree if focusedPath is set)
  const mergedTreeData = useMemo(() => {
    if (!displayTreeData) return null;

    // Deep clone, merge lazy-loaded children, and set parent refs
    const mergeChildren = (
      nodes: CloudTreeNode[],
      parent: CloudTreeNode | undefined,
    ): CloudTreeNode[] => {
      return nodes.map((node) => {
        const lazyChildren = lazyLoadedChildren.get(node.metadata.path);

        // Create new node with parent reference
        const newNode: CloudTreeNode = {
          ...node,
          parent,
          children: [], // Will be set below
        };

        // If this node has lazy-loaded children, use them
        if (lazyChildren) {
          newNode.children = mergeChildren(lazyChildren, newNode);
        }
        // Otherwise, recursively merge any children
        else if (node.children && node.children.length > 0) {
          newNode.children = mergeChildren(node.children, newNode);
        }

        return newNode;
      });
    };

    return mergeChildren(displayTreeData, undefined);
  }, [displayTreeData, lazyLoadedChildren]);

  /**
   * Load children for a folder that hasn't been loaded yet.
   * @param folderPath - The path of the folder to load
   * @param forceReload - If true, reload even if already loaded (for retry)
   */
  const loadFolderChildren = useCallback(
    async (folderPath: string, forceReload = false) => {
      // Already loading (use ref to get current state, not stale closure)
      if (loadingPathsRef.current.has(folderPath)) {
        return;
      }

      // Already loaded (check lazy-loaded children) - skip unless force reload
      if (!forceReload && lazyLoadedChildren.has(folderPath)) {
        // Allow reload if previous load resulted in empty children (might have been a bug)
        const existingChildren = lazyLoadedChildren.get(folderPath);
        if (existingChildren && existingChildren.length > 0) {
          return;
        }
      }

      // Mark as loading in ref
      loadingPathsRef.current.add(folderPath);

      // Set loading state
      setNodeLoadingStates((prev) => {
        const next = new Map(prev);
        next.set(folderPath, { status: "loading" });
        return next;
      });

      try {
        // Fetch children with depth 1 (immediate children only for lazy loading)
        const children = await fetchCloudSubtree(
          providerType,
          connectionId,
          folderPath,
          1,
        );

        // The API returns the folder itself as root with children inside
        // We need to extract just the children
        // Check if the first item is the folder itself (path matches) with children
        // or if the API returned the children directly
        let childNodes: CloudTreeNode[] = [];

        if (children.length > 0) {
          const firstItem = children[0];
          // If first item's path matches the folder we're loading and it has children,
          // extract those children
          if (firstItem.metadata.path === folderPath && firstItem.children) {
            childNodes = firstItem.children;
          } else {
            // Otherwise, the API returned children directly (not nested)
            childNodes = children;
          }
        }

        // Store loaded children
        setLazyLoadedChildren((prev) => {
          const next = new Map(prev);
          next.set(folderPath, childNodes);
          return next;
        });

        // Mark as loaded
        setNodeLoadingStates((prev) => {
          const next = new Map(prev);
          next.set(folderPath, { status: "loaded" });
          return next;
        });
      } catch (err) {
        // Set error state
        setNodeLoadingStates((prev) => {
          const next = new Map(prev);
          next.set(folderPath, {
            status: "error",
            error: (err as Error).message || "Failed to load folder contents",
          });
          return next;
        });
      } finally {
        // Remove from loading ref
        loadingPathsRef.current.delete(folderPath);
      }
    },
    [providerType, connectionId, lazyLoadedChildren],
  );

  /**
   * Prefetch truncated directories within a given node's children.
   * This is called when a folder is expanded or navigated to, so that
   * truncated subdirectories are loaded before the user tries to expand them.
   */
  const prefetchTruncatedChildren = useCallback(
    (nodes: CloudTreeNode[] | null | undefined) => {
      if (!nodes) return;

      for (const node of nodes) {
        // Check if this node is a truncated directory that needs loading
        if (node.metadata.is_directory && node.truncated) {
          // loadFolderChildren handles deduplication via ref
          loadFolderChildren(node.metadata.path);
        }
      }
    },
    [loadFolderChildren],
  );

  // When navigating to a folder (focusedPath changes), load its children if needed
  useEffect(() => {
    if (!focusedPath || focusedPath === "/" || focusedPath === rootPath) {
      return;
    }

    // Find the focused node in the tree
    const findNode = (
      nodes: CloudTreeNode[],
      targetPath: string,
    ): CloudTreeNode | null => {
      for (const node of nodes) {
        if (node.metadata.path === targetPath) {
          return node;
        }
        if (node.children && targetPath.startsWith(node.metadata.path + "/")) {
          const found = findNode(node.children, targetPath);
          if (found) return found;
        }
      }
      return null;
    };

    if (treeData) {
      const focusedNode = findNode(treeData, focusedPath);

      // If the focused folder is truncated or has no children, load them
      if (focusedNode?.metadata.is_directory) {
        const hasNoChildren =
          !focusedNode.children || focusedNode.children.length === 0;
        const isTruncated = focusedNode.truncated;

        if (hasNoChildren || isTruncated) {
          loadFolderChildren(focusedPath);
        }
      }
    }
  }, [focusedPath, rootPath, treeData, loadFolderChildren]);

  // Prefetch truncated children when the displayed tree changes (e.g., navigation)
  useEffect(() => {
    if (displayTreeData && displayTreeData.length > 0) {
      prefetchTruncatedChildren(displayTreeData);
    }
  }, [displayTreeData, prefetchTruncatedChildren]);

  /**
   * Retry loading a failed folder.
   */
  const handleRetryNode = useCallback(
    (folderPath: string) => {
      // Reset state and reload
      setNodeLoadingStates((prev) => {
        const next = new Map(prev);
        next.set(folderPath, { status: "idle" });
        return next;
      });
      loadFolderChildren(folderPath);
    },
    [loadFolderChildren],
  );

  /**
   * Check if a folder needs lazy loading (has no children and hasn't been loaded).
   */
  const needsLazyLoad = useCallback(
    (node: CloudTreeNode): boolean => {
      if (!node.metadata.is_directory) return false;

      // Has children from initial load
      if (node.children && node.children.length > 0) return false;

      // Has lazy-loaded children
      if (lazyLoadedChildren.has(node.metadata.path)) return false;

      // Check loading state
      const state = nodeLoadingStates.get(node.metadata.path);
      if (state?.status === "loading" || state?.status === "loaded")
        return false;

      return true;
    },
    [lazyLoadedChildren, nodeLoadingStates],
  );

  /**
   * Find node by path in the tree.
   */
  const findNodeByPath = useCallback(
    (path: string, nodes: CloudTreeNode[] | null): CloudTreeNode | null => {
      if (!nodes) return null;
      for (const node of nodes) {
        if (node.metadata.path === path) return node;
        if (node.children) {
          const found = findNodeByPath(path, node.children);
          if (found) return found;
        }
        // Also check lazy-loaded children
        const lazyChildren = lazyLoadedChildren.get(node.metadata.path);
        if (lazyChildren) {
          const found = findNodeByPath(path, lazyChildren);
          if (found) return found;
        }
      }
      return null;
    },
    [lazyLoadedChildren],
  );

  // Handle node toggle (expand/collapse) with lazy loading
  const handleNodeToggle = useCallback(
    (_event: React.SyntheticEvent, itemIds: string[]) => {
      const newExpandedSet = new Set(itemIds);
      const previousExpandedSet = expandedNodes;

      // Find newly expanded nodes
      const newlyExpanded = itemIds.filter(
        (id) => !previousExpandedSet.has(id),
      );

      setExpandedNodes(newExpandedSet);

      // Trigger lazy loading for newly expanded folders that need it
      for (const path of newlyExpanded) {
        // Skip placeholder/loading/error item IDs
        if (path.includes("/__")) continue;

        const node = findNodeByPath(path, mergedTreeData);
        if (node) {
          if (needsLazyLoad(node)) {
            loadFolderChildren(path);
          }
          // Prefetch truncated children of the expanded folder
          // so they're ready when the user expands them
          prefetchTruncatedChildren(node.children);
        }
      }
    },
    [
      expandedNodes,
      mergedTreeData,
      needsLazyLoad,
      loadFolderChildren,
      findNodeByPath,
      prefetchTruncatedChildren,
    ],
  );

  // Handle item selection
  const handleToggleSelect = useCallback(
    (node: CloudTreeNode) => {
      const item = node.metadata;
      if (isControlled) {
        // In controlled mode, notify parent via callback
        if (onSelectionChange) {
          const currentFiles = controlledSelectedFiles || [];
          const currentFolders = controlledSelectedFolders || [];

          // Check selection states
          const isDirectlySelected = item.is_directory
            ? currentFolders.some((f) => f.path === item.path)
            : currentFiles.some((f) => f.path === item.path);

          const isImplicitlySelected = isChildOfAnyFolder(node, currentFolders);

          if (isDirectlySelected) {
            // Directly selected - just remove it
            if (item.is_directory) {
              onSelectionChange(
                currentFiles,
                currentFolders.filter((f) => f.path !== item.path),
              );
            } else {
              onSelectionChange(
                currentFiles.filter((f) => f.path !== item.path),
                currentFolders,
              );
            }
          } else if (isImplicitlySelected) {
            // Implicitly selected via ancestor folder - need to "punch a hole"
            // Use BUBBLING: start from clicked item and walk UP to ancestor,
            // adding siblings at each level.
            const selectedAncestor = findSelectedAncestor(node, currentFolders);

            if (selectedAncestor) {
              let newFolders = currentFolders.filter(
                (f) => f.path !== selectedAncestor.metadata.path,
              );
              let newFiles = [...currentFiles];

              // Start from the clicked item and bubble UP to the ancestor
              let currentNode: CloudTreeNode | undefined = node;

              while (currentNode) {
                // Stop BEFORE processing if we've reached the selected ancestor
                // We should not add siblings of the ancestor folder itself
                if (
                  currentNode.metadata.path === selectedAncestor.metadata.path
                ) {
                  break;
                }

                // Get siblings of current item (excludes current item itself)
                const siblings = getSiblings(currentNode);

                // Add all siblings to selection
                for (const sibling of siblings) {
                  if (sibling.metadata.is_directory) {
                    if (
                      !newFolders.some((f) => f.path === sibling.metadata.path)
                    ) {
                      newFolders = [...newFolders, sibling.metadata];
                    }
                  } else {
                    if (
                      !newFiles.some((f) => f.path === sibling.metadata.path)
                    ) {
                      newFiles = [...newFiles, sibling.metadata];
                    }
                  }
                }

                // Move up to parent using the tree-based parent ref
                currentNode = currentNode.parent;
              }

              onSelectionChange(newFiles, newFolders);
            }
          } else {
            // Not selected - add it
            if (item.is_directory) {
              // When selecting a folder, remove any descendants that are already selected
              // (they become implicitly selected via the folder)
              const filteredFiles = currentFiles.filter(
                (f) => !isDescendantOf(f.path, item.path),
              );
              const filteredFolders = currentFolders.filter(
                (f) => !isDescendantOf(f.path, item.path),
              );

              // Check if selecting this folder completes selection of all siblings
              // If so, promote to parent
              const promoted = promoteSelectionToParent(node, filteredFiles, [
                ...filteredFolders,
                item,
              ]);
              onSelectionChange(promoted.files, promoted.folders);
            } else {
              // Add the file, then check if all siblings are now selected
              const newFiles = [...currentFiles, item];
              const promoted = promoteSelectionToParent(
                node,
                newFiles,
                currentFolders,
              );
              onSelectionChange(promoted.files, promoted.folders);
            }
          }
        }

        // Still call onFileSelect for file clicks
        if (!item.is_directory && onFileSelect) {
          onFileSelect(item);
        }
      } else {
        // In uncontrolled mode, manage state locally
        // TODO: Add implicit selection support for uncontrolled mode if needed
        if (item.is_directory) {
          setInternalSelectedFolders((prev) => {
            const next = new Map(prev);
            if (next.has(item.path)) {
              next.delete(item.path);
            } else {
              next.set(item.path, item);
            }
            return next;
          });
        } else {
          setInternalSelectedFiles((prev) => {
            const next = new Map(prev);
            if (next.has(item.path)) {
              next.delete(item.path);
            } else {
              next.set(item.path, item);
            }
            return next;
          });

          if (onFileSelect) {
            onFileSelect(item);
          }
        }
      }
    },
    [
      isControlled,
      onFileSelect,
      onSelectionChange,
      controlledSelectedFiles,
      controlledSelectedFolders,
    ],
  );

  // Error state
  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          data-testid="cloud-tree-error"
          action={
            <IconButton size="small" onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          }
        >
          Failed to load files: {(error as Error)?.message || "Unknown error"}
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (isLoading) {
    return <TreeSkeleton />;
  }

  // Empty state
  if (!mergedTreeData || mergedTreeData.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          color: "text.secondary",
        }}
      >
        <FolderOpenIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
        <Typography>No files or folders found</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={treeContainerRef}
      sx={{
        height,
        overflow: "auto",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "grey.50",
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {(focusedPath || rootPath) === "/" ? "Root" : focusedPath || rootPath}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={() => refetch()}
            data-testid="cloud-tree-refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tree View */}
      <SimpleTreeView
        slots={{
          collapseIcon: ExpandMoreIcon,
          expandIcon: ChevronRightIcon,
        }}
        expandedItems={expandedArray}
        onExpandedItemsChange={handleNodeToggle}
        sx={{ p: 1 }}
        data-testid="cloud-tree"
      >
        {renderTree({
          nodes: mergedTreeData,
          selectionMode,
          showDetails,
          selectedFiles,
          selectedFolders,
          allSelectedPaths,
          onToggleSelect: handleToggleSelect,
          onFolderNavigate,
          expandedNodes,
          nodeLoadingStates,
          onRetryNode: handleRetryNode,
          folderExtensionFilters,
          inheritedExtensionFilter,
        })}
      </SimpleTreeView>
    </Box>
  );
};

export default CloudFileTree;
